/**
 * Cart Drawer Custom Element
 * Built with Web Components for better encapsulation and performance
 */

class CartDrawer extends HTMLElement {
    constructor() {
        super();
        
        // Component state
        this.isInitialized = false;
        this.sectionId = 'minicart';
        this.sectionsToRender = ['minicart'];
        
        // Cached selectors
        this.selectors = {
            drawer: '#CartDrawer',
            overlay: '#CartDrawer-Overlay',
            closeButton: '.drawer__close',
            cartButton: '.cartButton',
            cartCounter: '.cartCounter',
            cartItems: '#CartDrawer-CartItems',
            emptyState: '.drawer__empty-content',
            cartFooter: '.drawer__footer',
            quantityInput: '.quantity__input',
            quantityButtons: '.quantity__button',
            removeButtons: '.cart-item__remove',
            checkoutButton: '#CartDrawer-Checkout',
            continueButton: '.drawer__continue',
            // New selectors for enhanced features
            shippingProgress: '.drawer__shipping-progress',
            progressBar: '.shipping-progress__fill',
            progressText: '.shipping-progress__text',
            recommendationAddBtn: '.recommendation-product__add-btn'
        };
        
        this.classes = {
            active: 'active',
            loading: 'loading',
            error: 'error',
            empty: 'is-empty'
        };
        
        // Cached DOM elements
        this.elements = {};
        
        // Bind methods to preserve context
        this.handleClick = this.handleClick.bind(this);
        this.handleChange = this.handleChange.bind(this);
        this.handleKeydown = this.handleKeydown.bind(this);
        this.handleRecommendationAdd = this.handleRecommendationAdd.bind(this);
    }
    
    connectedCallback() {
        if (!this.isInitialized) {
            this.init();
        }
    }
    
    disconnectedCallback() {
        this.cleanup();
    }
    
    /**
     * Initialize the cart drawer
     */
    init() {
        if (this.isInitialized) return;
        
        this.cacheElements();
        this.detectSectionId();
        this.bindEvents();
        this.updateCartCount();
        this.loadRecommendations();
        this.updateShippingProgressFromCart();
        this.isInitialized = true;
    }
    
    /**
     * Cache DOM elements for better performance
     */
    cacheElements() {
        this.elements.drawer = document.querySelector(this.selectors.drawer);
        this.elements.overlay = document.querySelector(this.selectors.overlay);
        this.elements.cartCounters = document.querySelectorAll(this.selectors.cartCounter);
        
        if (!this.elements.drawer || !this.elements.overlay) {
            return;
        }
    }
    
    /**
     * Auto-detect section ID from DOM
     */
    detectSectionId() {
        const section = this.closest('[id*="shopify-section"][id*="minicart"]') ||
                       document.querySelector('[id*="shopify-section"][id*="minicart"]');
        
        if (section) {
            const matches = section.id.match(/shopify-section-(.+)/);
            if (matches && matches[1]) {
                this.sectionId = matches[1];
                this.sectionsToRender = [this.sectionId];
                return;
            }
        }
    }
    
    /**
     * Bind event listeners with delegation for better performance
     */
    bindEvents() {
        // Use event delegation only within this component's scope
        this.addEventListener('click', this.handleClick);
        this.addEventListener('change', this.handleChange);
        
        // Global events that need to be handled
        document.addEventListener('keydown', this.handleKeydown);
        
        // Listen for cart button clicks outside the component
        document.addEventListener('click', (e) => {
            if (e.target.closest(this.selectors.cartButton)) {
                e.preventDefault();
                this.open();
            }
        });
        
        // Listen for overlay clicks (overlay is outside component)
        if (this.elements.overlay) {
            this.overlayClickHandler = () => {
                this.close();
            };
            this.elements.overlay.addEventListener('click', this.overlayClickHandler);
        }
        
        // Listen for cart JSON updates to keep drawer in sync
        this.cartUpdateHandler = (event) => {
            this.updateCartCount();
            this.updateShippingProgressFromCart();
        };
        window.addEventListener('cartjson:updated', this.cartUpdateHandler);
    }
    
    /**
     * Handle click events within the component
     */
    handleClick(e) {
        const target = e.target;
        
        // Close button
        if (
            target.closest(this.selectors.closeButton) 
            || target.closest(this.selectors.overlay) 
            || target.matches(this.selectors.overlay)
            || target.closest(this.selectors.continueButton)
         ) {
            e.preventDefault();
            this.close();
            return;
        }
        
        // Quantity buttons
        if (target.closest(this.selectors.quantityButtons)) {
            e.preventDefault();
            this.handleQuantityButtons(e);
            return;
        }
        
        // Remove buttons
        if (target.closest(this.selectors.removeButtons)) {
            e.preventDefault();
            this.handleRemoveItem(e);
            return;
        }
        
        // Checkout button
        if (target.closest(this.selectors.checkoutButton)) {
            e.preventDefault();
            window.location.href = window.Shopify.routes.root + 'checkout';
            return;
        }
        
        // Recommendation product add buttons
        if (target.closest(this.selectors.recommendationAddBtn)) {
            e.preventDefault();
            this.handleRecommendationAdd(e);
            return;
        }
        
    }
    
    /**
     * Handle change events within the component
     */
    handleChange(e) {
        if (e.target.matches(this.selectors.quantityInput)) {
            this.handleQuantityChange(e);
        }
        
    }
    
    /**
     * Handle keydown events (ESC to close)
     */
    handleKeydown(e) {
        if (e.key === 'Escape' && this.isOpen()) {
            this.close();
        }
    }
    
    /**
     * Check if drawer is open
     */
    isOpen() {
        return this.elements.drawer && this.elements.drawer.classList.contains(this.classes.active);
    }
    
    /**
     * Open the drawer
     */
    open() {
        if (!this.elements.drawer || !this.elements.overlay) return;
        
        this.elements.drawer.classList.add(this.classes.active);
        this.elements.overlay.classList.add(this.classes.active);
        document.body.classList.add('open-cart-drawer');
        document.documentElement.classList.add('open-cart-drawer');
        this.focusFirstElement();
    }
    
    /**
     * Close the drawer
     */
    close() {
        if (!this.elements.drawer || !this.elements.overlay) return;
        
        this.elements.drawer.classList.remove(this.classes.active);
        this.elements.overlay.classList.remove(this.classes.active);
        document.body.classList.remove('open-cart-drawer');
        document.documentElement.classList.remove('open-cart-drawer');
    }
    
    /**
     * Focus first focusable element for accessibility
     */
    focusFirstElement() {
        const firstFocusable = this.elements.drawer.querySelector(
            'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        if (firstFocusable) {
            firstFocusable.focus();
        }
    }
    
    /**
     * Handle quantity input changes
     */
    handleQuantityChange(e) {
        const input = e.target;
        const variantId = input.dataset.index;
        let quantity = parseInt(input.value) || 0;
        
        if (quantity < 0) {
            quantity = 0;
            input.value = quantity;
        }
        
        const cartItem = input.closest('.cart-item');
        this.updateCartItem(cartItem, variantId, quantity);
    }
    
    /**
     * Handle quantity plus/minus buttons
     */
    handleQuantityButtons(e) {
        const button = e.target.closest(this.selectors.quantityButtons);
        const quantityWrapper = button.closest('.quantity');
        const input = quantityWrapper.querySelector(this.selectors.quantityInput);
        const currentQuantity = parseInt(input.value) || 0;
        const variantId = input.dataset.index;
        const cartItem = input.closest('.cart-item');
        
        let newQuantity;
        if (button.name === 'plus') {
            newQuantity = currentQuantity + 1;
        } else if (button.name === 'minus') {
            newQuantity = Math.max(0, currentQuantity - 1);
        }
        
        input.value = newQuantity;
        this.updateCartItem(cartItem, variantId, newQuantity);
    }
    
    /**
     * Handle remove item
     */
    handleRemoveItem(e) {
        const button = e.target.closest(this.selectors.removeButtons);
        const variantId = button.dataset.index;
        const cartItem = button.closest('.cart-item');
        
        this.updateCartItem(cartItem, variantId, 0);
    }
    
    /**
     * Handle recommendation product add to cart
     */
    async handleRecommendationAdd(e) {
        const button = e.target.closest(this.selectors.recommendationAddBtn);
        const productCard = button.closest('.recommendation-product');
        
        if (!productCard) return;
        
        const variantId = button.dataset.variantId;
        const productTitle = button.dataset.productTitle || 
                            productCard.querySelector('.recommendation-product__title')?.textContent?.trim();
        
        if (!variantId) {
            return;
        }
        
        // Update button state
        const originalText = button.textContent;
        button.textContent = 'Adding...';
        button.disabled = true;
        
        try {
            await this.addToCart(variantId, 1);
            
            // Success feedback
            button.textContent = 'Added!';
            
            // Reset after delay
            setTimeout(() => {
                button.textContent = 'Add';
                button.disabled = false;
            }, 2000);
            
        } catch (error) {
            
            // Error feedback
            button.textContent = 'Try Again!';
            
            // Reset after delay
            setTimeout(() => {
                button.textContent = 'Add';
                button.disabled = false;
            }, 2000);
        }
    }
    
    /**
     * Load recommendations based on cart items using cached cart data
     */
    async loadRecommendations() {
        const recommendationsContainer = document.querySelector('#CartDrawer-Recommendations');
        if (!recommendationsContainer || recommendationsContainer.classList.contains('recommendations-loaded')) {
            return;
        }
        
        const productsContainer = recommendationsContainer.querySelector('.recommendations__products');
        if (!productsContainer) {
            return;
        }
        
        try {
            // Use cached cart data first (much faster)
            let productId = null;
            
            if (window.cartJSON?.items?.length > 0) {
                productId = window.cartJSON.items[0].product_id;
            }
            
            // Fallback to DOM if cache not available
            if (!productId) {
                const firstCartItem = document.querySelector('.cart-item');
                if (!firstCartItem) {
                    this.hideRecommendations();
                    return;
                }
                
                // Try multiple ways to get product ID from DOM
                productId = firstCartItem.dataset.productId || 
                           firstCartItem.dataset.variantId ||
                           firstCartItem.querySelector('[data-product-id]')?.dataset.productId ||
                           firstCartItem.querySelector('[data-variant-id]')?.dataset.variantId;
            }
            
            if (!productId) {
                this.hideRecommendations();
                return;
            }
            
            // Load recommendations using Section Rendering API
            await this.loadRecommendationsForProduct(productId);
            
        } catch (error) {
            console.error('loadRecommendations error', error);
            this.hideRecommendations();
        }
    }
    
    /**
     * Load recommendations for a specific product using Section Rendering API only
     */
    async loadRecommendationsForProduct(productId) {
        
        const sectionId = 'product-recommendations';
        const params = new URLSearchParams({
            product_id: productId,
            limit: 6,
            intent: 'related',
            section_id: sectionId
        });
        
        try {
            const response = await fetch(`/recommendations/products?${params}`, {
                method: 'GET',
                headers: {
                    'X-Requested-With': 'XMLHttpRequest'
                }
            });
            
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }
            
            const html = await response.text();
            this.updateRecommendationsHTML(html);
            
        } catch (error) {
            console.error('loadRecommendationsForProduct error', error);
            this.hideRecommendations();
        }
    }
    
    /**
     * Update recommendations container with new HTML
     */
    updateRecommendationsHTML(html) {
        const recommendationsContainer = document.querySelector('#CartDrawer-Recommendations');
        const swiperWrapper = recommendationsContainer?.querySelector('.swiper-wrapper');
        
        if (!swiperWrapper) return;
        
        // Extract the slide content from the section HTML
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = html;
        
        // Look for swiper slides in the response
        const newSlides = tempDiv.querySelectorAll('.swiper-slide');
        if (newSlides.length > 0) {
            swiperWrapper.innerHTML = '';
            newSlides.forEach(slide => {
                swiperWrapper.appendChild(slide.cloneNode(true));
            });
            swiperWrapper.setAttribute('data-recommendations-loaded', 'true');
            
            // Initialize Swiper for recommendations
            this.initRecommendationsSwiper();
            
            // Show recommendations
            this.showRecommendations();
        } else {
            this.hideRecommendations();
        }
        recommendationsContainer.classList.add('recommendations-loaded');
    }

    /**
     * Initialize Swiper for recommendations slider
     */
    initRecommendationsSwiper() {
        // Destroy existing instance if it exists
        if (this.recommendationsSwiper) {
            this.recommendationsSwiper.destroy(true, true);
            this.recommendationsSwiper = null;
        }
        
        const swiperContainer = document.querySelector('.recommendations-swiper');
        if (!swiperContainer) return;
        
     
        this.recommendationsSwiper = new Swiper('.recommendations-swiper', {
            slidesPerView: 'auto',
            spaceBetween: 16,
            freeMode: {
                enabled: true,
                sticky: false,
            },
            navigation: {
                nextEl: '.swiper-button-next',
                prevEl: '.swiper-button-prev',
            },
            breakpoints: {
                640: {
                    slidesPerView: 'auto',
                    spaceBetween: 16,
                }
            }
        });
    }
    
    /**
     * Hide recommendations section
     */
    hideRecommendations() {
        const recommendationsContainer = document.querySelector('#CartDrawer-Recommendations');
        if (recommendationsContainer) {
            recommendationsContainer.style.display = 'none';
        }
    }
    
    /**
     * Show recommendations section
     */
    showRecommendations() {
        const recommendationsContainer = document.querySelector('#CartDrawer-Recommendations');
        if (recommendationsContainer) {
            recommendationsContainer.style.display = 'block';
        }
    }
    
    /**
     * Update free shipping progress bar and text display
     */
    updateShippingProgress(cartTotal) {
        const progressBar = document.querySelector(this.selectors.progressBar);
        const processTextEl = document.querySelector('.js-process-text');
        
        if (!progressBar || !processTextEl) return;
        
        // Find the message element regardless of current attributes
        const processMessage = processTextEl.querySelector('[data-process-message], [data-success-message]') || 
                              processTextEl.querySelector('div:first-child');
        
        if (!processMessage) return;
        
        const freeShippingThreshold = parseInt(document.querySelector('.drawer__shipping-progress').dataset.shippingThreshold);
        const progressPercentage = Math.min((cartTotal / freeShippingThreshold) * 100, 100);
        
        // Update progress bar width
        progressBar.style.width = progressPercentage + '%';
        
        // Show appropriate text based on shipping qualification
        if (cartTotal >= freeShippingThreshold) {
            // Qualified for free shipping - show complete text
            processMessage.innerHTML = processTextEl.dataset.successText;
            processMessage.setAttribute('data-success-message', '');
            processMessage.removeAttribute('data-process-message');
        } else {
            // Not qualified - show process text with updated remaining amount
            const remainingAmount = freeShippingThreshold - cartTotal;
            const remainingFormatted = '$' + remainingAmount.toFixed(2);
            const processTemplate = processTextEl.dataset.processText;
            processMessage.innerHTML = processTemplate.replace('[x]', remainingFormatted);
            processMessage.setAttribute('data-process-message', '');
            processMessage.removeAttribute('data-success-message');
        }
    }
    
    /**
     * Update shipping progress from cached cart data
     */
    updateShippingProgressFromCart() {
        if (!window.cartJSON?.total_price) return;
        
        const cartTotal = window.cartJSON.total_price / 100;
        this.updateShippingProgress(cartTotal);
    }
    
    /**
     * Update cart counter display
     */
    updateCartCount() {
        const count = window.cartJSON?.item_count || 0;
        this.elements.cartCounters.forEach(counter => {
            counter.textContent = count;
            counter.style.display = count === 0 ? 'none' : '';
        });
    }
    
    /**
     * Get Cart API instance
     */
    get cartAPI() {
        return {
            addItem: async (data, sections) => {
                const requestData = { ...data };
                if (sections && sections.length > 0) {
                    requestData.sections = sections.join(',');
                    requestData.sections_url = window.location.pathname;
                }
                
                const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-Requested-With': 'XMLHttpRequest'
                    },
                    body: JSON.stringify(requestData)
                });
                
                if (!response.ok) {
                    const error = await response.json();
                    throw new Error(error.message || 'Failed to add item');
                }
                
                const result = await response.json();
                
                // Update cart JSON cache manually if theme.CartAPI not available
                if (window.updateCartJSON) {
                    // Fetch fresh cart data after add
                    const cartResponse = await fetch(window.Shopify.routes.root + 'cart.js', {
                        headers: {
                            'Content-Type': 'application/json',
                            'X-Requested-With': 'XMLHttpRequest'
                        }
                    });
                    const cart = await cartResponse.json();
                    window.updateCartJSON(cart);
                }
                
                return result;
            },
            
            updateItem: async (variantId, quantity, sections) => {
                const requestData = { id: variantId, quantity };
                if (sections && sections.length > 0) {
                    requestData.sections = sections.join(',');
                    requestData.sections_url = window.location.pathname;
                }
                
                const response = await fetch(window.Shopify.routes.root + 'cart/change.js', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-Requested-With': 'XMLHttpRequest'
                    },
                    body: JSON.stringify(requestData)
                });
                
                const cart = await response.json();
                
                // Update cart JSON cache manually if theme.CartAPI not available
                if (window.updateCartJSON) {
                    window.updateCartJSON(cart);
                }
                
                return cart;
            },
            
            getCart: async () => {
                const response = await fetch(window.Shopify.routes.root + 'cart.js', {
                    headers: {
                        'Content-Type': 'application/json',
                        'X-Requested-With': 'XMLHttpRequest'
                    }
                });
                const cart = await response.json();
                
                // Update cart JSON cache manually if theme.CartAPI not available
                if (window.updateCartJSON) {
                    window.updateCartJSON(cart);
                }
                
                return cart;
            },
            
            getSections: async (sections, url) => {
                if (!sections || sections.length === 0) return {};
                
                const sectionsParam = sections.join(',');
                const baseUrl = url || window.location.pathname;
                const requestUrl = `${baseUrl}?sections=${encodeURIComponent(sectionsParam)}`;
                
                const response = await fetch(requestUrl, {
                    headers: {
                        'Content-Type': 'application/json',
                        'X-Requested-With': 'XMLHttpRequest'
                    }
                });
                
                return response.json();
            }
        };
    }
    
    /**
     * Add item to cart and update drawer
     */
    async addToCart(variantId, quantity = 1, properties = null) {
        const data = { id: variantId, quantity };
        if (properties) data.properties = properties;
        
        try {
            // Open drawer first for instant feedback
            this.open();
            
            const response = await this.cartAPI.addItem(data, this.sectionsToRender);
            
            this.updateCartContent(response.sections);

            return response;
            
        } catch (error) {
            console.error('addToCart error', error);
            throw error;
        }
    }
    
    /**
     * Update cart item with simple full refresh
     */
    async updateCartItem(cartItem, variantId, quantity) {
        if (!cartItem || !variantId) return;
        
        // Show loading state
        cartItem.classList.add(this.classes.loading);
        
        try {
            const response = await this.cartAPI.updateItem(variantId, quantity, this.sectionsToRender);
            
            this.updateCartContent(response.sections);
        } catch (error) {
            cartItem.classList.remove(this.classes.loading);
            console.error('updateCartItem error', error);
        }
    }
    
    /**
     * Update cart content with smooth animation
     */
    updateCartContent(sections) {
        if (!sections || !sections[this.sectionId]) {
            return;
        }
        const drawer = this.elements.drawer;
        if (!drawer) return;
        const tempContainer = document.createElement('div');
        tempContainer.innerHTML = sections[this.sectionId];
        // If empty cart layout is present in new content, replace entire drawer
        const emptyContent = tempContainer.querySelector('.drawer__empty-content');
        if (emptyContent) {
            const newInner = tempContainer.querySelector('.drawer__inner');
            const currentInner = drawer.querySelector('.drawer__inner');
            if (newInner && currentInner) {
                currentInner.replaceWith(newInner);
            } else {
                drawer.innerHTML = tempContainer.querySelector('.drawer__inner').innerHTML;
            }
            this.cacheElements();
            this.loadRecommendations();
            return;
        }
        if (!drawer.querySelector('.drawer__cart-items')) {
            const newInner = tempContainer.querySelector('.drawer__inner');
            const currentInner = drawer.querySelector('.drawer__inner');
            if (newInner && currentInner) {
                currentInner.replaceWith(newInner);
            } else {
                drawer.innerHTML = tempContainer.querySelector('.drawer__inner').innerHTML;
            }
            this.cacheElements();
            this.loadRecommendations();
            return;
        }
        // Otherwise, update cart items and drawer bottom only
        const newCartItems = tempContainer.querySelector('.drawer__cart-items');
        const currentCartItems = drawer.querySelector('.drawer__cart-items');
        if (newCartItems && currentCartItems) {
            currentCartItems.innerHTML = newCartItems.innerHTML;
        }
        const newDrawerBottom = tempContainer.querySelector('.drawer__bottom');
        const currentDrawerBottom = drawer.querySelector('.drawer__bottom');
        if (newDrawerBottom && currentDrawerBottom) {
            currentDrawerBottom.innerHTML = newDrawerBottom.innerHTML;
        }
        this.cacheElements();
    }
    
    /**
     * Refresh cart content
     */
    async refreshCart() {
        try {
            const sections = await this.cartAPI.getSections(this.sectionsToRender);
            this.updateCartContent(sections);
        } catch (error) {
            this.fallbackRefresh();
        }
    }
    
    /**
     * Fallback refresh when section rendering fails
     */
    fallbackRefresh() {
        try {
            // Use cached cart data (no API call needed)
            this.updateCartCount(); // Will use cached count
        } catch (error) {
            console.error('fallbackRefresh error', error);
        }
    }
    
    /**
     * Cleanup event listeners
     */
    cleanup() {
        // Clean up Swiper instance
        if (this.recommendationsSwiper) {
            this.recommendationsSwiper.destroy(true, true);
            this.recommendationsSwiper = null;
        }
        
        this.removeEventListener('click', this.handleClick);
        this.removeEventListener('change', this.handleChange);
        document.removeEventListener('keydown', this.handleKeydown);
        
        // Clean up overlay event listener
        if (this.elements.overlay && this.overlayClickHandler) {
            this.elements.overlay.removeEventListener('click', this.overlayClickHandler);
        }
        
        // Clean up cart update listener
        if (this.cartUpdateHandler) {
            window.removeEventListener('cartjson:updated', this.cartUpdateHandler);
        }
        
        this.isInitialized = false;
    }
    
    /**
     * Public API methods
     */
    
    async getCart(forceRefresh = false) {
        if (!forceRefresh && window.getCartJSON) {
            return window.getCartJSON();
        }
        return this.cartAPI.getCart();
    }
    
    getCartData() {
        return {
            items: window.cartUtils?.getItems() || [],
            itemCount: window.cartUtils?.getItemCount() || 0,
            totalPrice: window.cartUtils?.getTotalPrice() || 0,
            isEmpty: window.cartUtils?.isEmpty() || true,
            raw: window.cartJSON
        };
    }
    
    toggle() {
        this.isOpen() ? this.close() : this.open();
    }
}

// Register the custom element
if (!customElements.get('cart-drawer')) {
    customElements.define('cart-drawer', CartDrawer);
}

// Global cart drawer instance and helper functions
window.CartDrawerComponent = {
    getInstance() {
        return document.querySelector('cart-drawer') || 
               document.querySelector('[is="cart-drawer"]');
    },
    
    async addToCart(variantId, quantity, properties) {
        const instance = this.getInstance();
        if (!instance) throw new Error('Cart drawer not found');
        return instance.addToCart(variantId, quantity, properties);
    },
    
    open() {
        this.getInstance()?.open();
    },
    
    close() {
        this.getInstance()?.close();
    },
    
    toggle() {
        this.getInstance()?.toggle();
    },
    
    getCartData() {
        const instance = this.getInstance();
        if (instance?.getCartData) {
            return instance.getCartData();
        }
        
        return {
            items: window.cartUtils?.getItems() || [],
            itemCount: window.cartUtils?.getItemCount() || 0,
            totalPrice: window.cartUtils?.getTotalPrice() || 0,
            isEmpty: window.cartUtils?.isEmpty() || true,
            raw: window.cartJSON
        };
    }
};

// Export for modules
if (typeof module !== 'undefined' && module.exports) {
    module.exports = CartDrawer;
} 